import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { LANDING_LEAD_MODAL_SESSION_KEYS } from '@/hooks/use-landing-lead-modal-trigger'
import apiClient from '@/lib/api-client'
import { cn } from '@/lib/utils'
import { type WhitepaperLeadFormData, whitepaperLeadSchema } from '@/lib/whitepaper-lead-schema'
import { getFeaturedWhitepaper } from '@/lib/whitepapers'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'

/** Outer shell corner radius (px) — Figma lead modal frame. */
const MODAL_SHELL_RADIUS_PX = 24

const inputClassName =
  'h-auto rounded-lg border border-[#d5d7da] bg-white px-3 py-2 text-base leading-6 text-[#0a0a0a] shadow-[0px_1px_2px_0px_rgba(10,13,18,0.05)] placeholder:text-[#717680] focus-visible:ring-2 focus-visible:ring-[#0050e6]/30'

type WhitepaperLeadModalProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function WhitepaperLeadModal({ open, onOpenChange }: WhitepaperLeadModalProps) {
  const featured = getFeaturedWhitepaper()
  const [submitSuccess, setSubmitSuccess] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<WhitepaperLeadFormData>({
    resolver: zodResolver(whitepaperLeadSchema),
  })

  useEffect(() => {
    if (open) {
      reset()
      setSubmitSuccess(false)
    }
  }, [open, reset])

  const handleOpenChange = (next: boolean) => {
    if (!next && !submitSuccess) {
      try {
        sessionStorage.setItem(LANDING_LEAD_MODAL_SESSION_KEYS.dismissed, '1')
      } catch {
        // ignore
      }
    }
    onOpenChange(next)
  }

  const onSubmit = (data: WhitepaperLeadFormData) => {
    apiClient
      .post('/api/v1/whitepaper/request', {
        name: data.name,
        company: data.company,
        email: data.email,
        whitepaper_slug: featured.slug,
      })
      .catch(() => undefined)

    try {
      sessionStorage.setItem(LANDING_LEAD_MODAL_SESSION_KEYS.submitted, '1')
    } catch {
      // ignore
    }

    setSubmitSuccess(true)
    reset()
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        style={{ borderRadius: `${MODAL_SHELL_RADIUS_PX}px` }}
        className={cn(
          'fixed left-[50%] top-[50%] z-50 flex w-[calc(100%-2rem)] max-w-[720px] translate-x-[-50%] translate-y-[-50%] flex-col gap-0 overflow-hidden border-0 bg-pelgo-blue-light p-0 duration-200',
          'shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)]',
          'max-h-[min(90vh,640px)] md:max-h-[min(90vh,520px)]',
          'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]',
          'md:flex-row md:items-stretch',
        )}
      >
        {/* Corners use 24px to match MODAL_SHELL_RADIUS_PX; shell bg is blue so subpixel gaps are not white. */}
        <div className="relative h-48 w-full shrink-0 overflow-hidden bg-pelgo-blue-light md:h-auto md:w-[220px] md:min-h-full md:self-stretch rounded-t-[24px] md:rounded-t-none md:rounded-l-[24px]">
          <img
            src={featured.modalHeroSrc}
            alt={featured.coverImageAlt}
            loading="lazy"
            className="h-full w-full object-cover object-center"
          />
        </div>

        <div className="relative flex min-h-0 min-w-0 flex-1 flex-col justify-between overflow-y-auto rounded-b-[24px] bg-white p-8 pt-10 md:rounded-b-none md:rounded-r-[24px] md:pt-8">
          {submitSuccess ? (
            <div className="flex flex-col gap-4 pr-6">
              <h3 className="font-display text-2xl font-medium leading-[1.2] tracking-[-0.48px] text-[#0a0a0a]">
                Thank you!
              </h3>
              <p className="text-sm font-normal leading-[1.5] tracking-[-0.28px] text-[#0a0a0a]">
                Check your email for the download link.
              </p>
              <p className="text-xs font-normal leading-[1.5] tracking-[-0.24px] text-[#535862]">
                If tapping the email button does nothing, your ad blocker may be blocking the
                sender&apos;s link redirect. Try again with the blocker paused for that message, or
                copy the direct <span className="whitespace-nowrap">pelgo.com</span> link from the
                plain-text version of the email.
              </p>
              <Button
                type="button"
                className="mt-2 w-full rounded-full bg-[#0050e6] px-6 py-3 text-base font-semibold uppercase leading-6 text-white hover:bg-[#0050e6]/90"
                onClick={() => handleOpenChange(false)}
              >
                Close
              </Button>
            </div>
          ) : (
            <>
              <DialogHeader className="space-y-2 p-0 pr-8 text-left">
                <DialogTitle className="font-display text-2xl font-medium leading-[1.2] tracking-[-0.48px] text-[#0a0a0a]">
                  Get the 2026 Industry Report
                </DialogTitle>
                <DialogDescription className="text-left text-sm font-normal leading-[1.5] tracking-[-0.28px] text-[#0a0a0a]">
                  {featured.heroDescription}
                </DialogDescription>
              </DialogHeader>

              <form
                onSubmit={handleSubmit(onSubmit)}
                className="mt-4 flex flex-col gap-2 pr-2 md:mt-4 md:flex-1 md:justify-between"
              >
                <div className="flex flex-col gap-2">
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="lead-modal-name" className="sr-only">
                      Name (required)
                    </Label>
                    <Input
                      id="lead-modal-name"
                      type="text"
                      placeholder="Name*"
                      {...register('name')}
                      className={inputClassName}
                      aria-invalid={errors.name ? 'true' : 'false'}
                    />
                    {errors.name && <p className="text-sm text-red-600">{errors.name.message}</p>}
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="lead-modal-email" className="sr-only">
                      Email (required)
                    </Label>
                    <Input
                      id="lead-modal-email"
                      type="email"
                      placeholder="Email*"
                      {...register('email')}
                      className={inputClassName}
                      aria-invalid={errors.email ? 'true' : 'false'}
                    />
                    {errors.email && <p className="text-sm text-red-600">{errors.email.message}</p>}
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="lead-modal-company" className="sr-only">
                      Company (required)
                    </Label>
                    <Input
                      id="lead-modal-company"
                      type="text"
                      placeholder="Company*"
                      {...register('company')}
                      className={inputClassName}
                      aria-invalid={errors.company ? 'true' : 'false'}
                    />
                    {errors.company && (
                      <p className="text-sm text-red-600">{errors.company.message}</p>
                    )}
                  </div>
                </div>

                <Button
                  type="submit"
                  className="mt-4 w-full rounded-full bg-[#0050e6] px-6 py-3 text-base font-semibold uppercase leading-6 text-white hover:bg-[#0050e6]/90 md:mt-6"
                >
                  Get the Whitepaper
                </Button>
              </form>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
